<template>
    <component
        :is="componentName"
        ref="gridInstance"
        :meta="meta"
        :findText="findText"
        :filterPanelShow="filterPanelShow"
        :toolbarShow="toolbarShow"
        :selectionMode="selectionMode"
        :totalCountSelectedShow="totalCountSelectedShow"
        :filterRow="filterRow"
        :customContextMenu="customContextMenu"
        @toolbar-button-click="toolbarButtonClick"
        @user-settings-changed="onUserSettingsChanged"
        @grid-virtual-mounted="onGridVirtualMounted"
        @user-settings-save="onUserSettingsSave"
        @reset-user-settings="onResetUserSettings"
        @focusedRowChanging="onFocusRowChanging"
        @selection-changed="onSelectionChanged"
        :loadUserSettingsCallback="loadUserSettingsCallback"
        :set-context="setContext"
    />
</template>

<script>
export default {
    name: 'ai-grid-virtual-wrapper',
};
</script>
<script setup>
import { onMounted, ref } from 'vue';
import { mainState, setIsReloadValue } from '@/layouts/main-layout/main-layout-data';
import { deleteUserSettingsChanges, getUserSettingsChanges, setUserSettingsChanges } from '@/service/userdata-service';
import { tabState } from '@/layouts/main-layout/components/navigation-bars-data';

const props = defineProps({
    routedView: {
        type: String,
        default: () => '',
    },
    meta: {
        type: Object,
        default: () => {
            return { columns: [] };
        },
    },
    findText: {
        type: String,
        default: () => {
            return '';
        },
    },
    filterPanelShow: {
        type: Boolean,
        default: () => {
            return undefined;
        },
    },
    toolbarShow: {
        type: Boolean,
        default: true,
    },
    selectionMode: {
        type: String,
    },
    totalCountSelectedShow: {
        type: Boolean,
        default: () => true,
    },
    filterRow: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    customContextMenu: {
        type: Array,
        default: () => [],
    },
    setContext: {
        type: Function,
        default: null,
    },
    hasntRoute: {
        type: Boolean,
        default: false,
    },
});
const emits = defineEmits([
    'gridVirtualWrapperMounted',
    'toolbarButtonClick',
    'gridColumnsWidthChanged',
    'selectionChanged',
]);
const gridInstance = ref(null);
const componentName = ref('ai-grid-virtual');
const userSettingsRef = ref({});

onMounted(async () => {});

const onGridVirtualMounted = async () => {
    if (mainState.isReload) {
        setIsReloadValue(false);
    }

    if (props.meta.viewId === mainState.viewId) {
        await fetchAndSetUserSettings({ viewId: props.meta.viewId });
    }

    emits('gridVirtualWrapperMounted');
};

const loadUserSettingsCallback = async () => {
    return userSettingsRef.value;
};

const fetchAndSetUserSettings = async () => {
    if (props.hasntRoute) {
        const { result, settings } = await getUserSettingsChanges({ viewId: props.meta.viewId });
        result ? (userSettingsRef.value = settings) : (userSettingsRef.value = {});

        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].settings = settings;
        return;
    }

    if (Object.keys(tabState).length && tabState[window.location.pathname]?.settings) {
        userSettingsRef.value = tabState[window.location.pathname].settings;
        gridInstance.value?.gridInstance.gridInstance.state({ ...userSettingsRef.value });
    } else {
        const { result, settings } = await getUserSettingsChanges({ viewId: props.meta.viewId });
        if (result && settings) {
            userSettingsRef.value = settings;
            gridInstance.value?.gridInstance.gridInstance.state({ ...userSettingsRef.value });
        }

        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].settings = settings;
    }
};

const onUserSettingsSave = async () => {
    tabState[window.location.pathname] ??= {};
    tabState[window.location.pathname].settings = userSettingsRef.value;
    await setUserSettingsChanges({ settings: userSettingsRef.value, viewId: props.meta.viewId });
};

const onFocusRowChanging = e => {
    emits('focusedRowChanging', e);
};

const onSelectionChanged = e => {
    emits('selectionChanged', e);
};

const onResetUserSettings = async () => {
    userSettingsRef.value = {};
    gridInstance.value.gridInstance.gridInstance.state({});
    tabState[window.location.pathname] = {};
    await deleteUserSettingsChanges({ viewId: props.meta.viewId });
};

const onUserSettingsChanged = async (userSettings, autoSaveMode) => {
    if (autoSaveMode) {
        const args = {
            viewId: props.meta.viewId,
            settings: userSettings,
        };
        tabState[window.location.pathname] ??= {};
        tabState[window.location.pathname].settings = userSettings;
        await setUserSettingsChanges(args);
    } else {
        userSettingsRef.value = userSettings;
    }
};

const toolbarButtonClick = e => {
    emits('toolbarButtonClick', e);
};
const getUserSettings = () => {
    return gridInstance.value.getUserSettings();
};

defineExpose({
    gridInstance,
    getUserSettings,
    fetchAndSetUserSettings,
});
</script>
<style scoped></style>
